import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  createContext,
  Suspense,
  act
} from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
  redirect
} from 'react-router-dom';
import { supabase } from './utilities/supabase';
import { LogoLoader, Toaster } from '@core/components';
import Dashboard from './pages/dashboard/Dashboard';
import Login from './pages/auth/login/Login';
import Loading from './pages/auth/loading/Loading';
import Error from './pages/error/Error';
import NotFound from './pages/error/404';
import UserProfile from './models/UserProfile';
import { NotificationProvider } from '@core/components';
import MaintenancePage from './pages/error/Maintenance';
import {
  initializeSentry,
  Sentry,
  sentryCreateBrowserRouter
} from './utilities/sentry';
import { AiProvider } from '@core/components';
import { ChatPanelProvider } from './components/chat/ChatPanelContext';
import lazyWithReload from './utilities/lazyWithReload';

// Less-used pages load on demand (Login, Loading and the Dashboard shell stay eager).
const Signup = lazyWithReload(() => import('./pages/auth/signup/Signup'));
const ForgotPassword = lazyWithReload(() =>
  import('./pages/auth/forgotPassword/ForgotPassword')
);
const ResetPassword = lazyWithReload(() =>
  import('./pages/auth/resetPassword/ResetPassword')
);
const StylePages = lazyWithReload(() => import('./pages/styles/Styles'));
const MFA = lazyWithReload(() => import('./pages/auth/2fa/2FA'));
const XeroCallback = lazyWithReload(() =>
  import('./pages/dashboard/settings/tabs/integrations/XeroCallback')
);

export const UserProfileContext = createContext(null);
export const ThemeContext = createContext(null);

// Once per page load, before the router is created (tracing wraps createBrowserRouter).
if (window.location.hostname.includes('crm.pensiontracingservice.com')) {
  initializeSentry({
    enableFeedback: true,
    enableReplays: true,
    enableTracing: true
  });
  console.log('Sentry initialized');
}

// Keep the current profile object when a refetch returns the same data, so a
// repeated SIGNED_IN (e.g. the tab regaining focus) doesn't re-render every consumer.
const sameProfile = (a, b) => {
  if (a === b) return true;
  if (!a || !b) return false;
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
};

const keepIfSameProfile = next => prev => (sameProfile(prev, next) ? prev : next);

const keepIfSameSession = next => prev =>
  prev && next && prev.access_token === next.access_token ? prev : next;

const handleAiRequest = async ({ template, selectedText, customPrompt }) => {
  // Your app-specific AI implementation here
  const { data: response } = await supabase.functions.invoke(
    'integrations/add-ai/text-completion',
    {
      body: {
        model: {
          provider: 1,
          id: 28
        },
        details: {
          template: template
        },
        input: {
          content: selectedText,
          prompt: customPrompt
        }
      }
    }
  );

  return response;
};

const handleVoiceRequest = async audioBlob => {
  // Your app-specific AI implementation here
  const formData = new FormData();
  formData.append('file', audioBlob, 'recording.webm');

  try {
    const { data: response, error } = await supabase.functions.invoke(
      'integrations/add-ai/voice-completion',
      {
        body: formData
      }
    );

    if (error) {
      console.error('Error uploading audio:', error);
      return null; // Or handle the error as needed
    }

    return response;
  } catch (error) {
    console.error('Error uploading audio:', error);
    return null; // Or handle the error as needed
  }
};

const App = () => {
  const [session, setSession] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showLoadingScreen, setShowLoadingScreen] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [theme, setTheme] = useState('light');
  const [isBackendAvailable, setIsBackendAvailable] = useState(true);
  const [showMFA, setShowMFA] = useState(false);
  const [passwordRecovery, setPasswordRecovery] = useState(false);

  useEffect(() => {
    // react-scan is a dev-only tool: load it on demand so it never ships in prod.
    if (import.meta.env.DEV) {
      import('react-scan').then(({ scan }) => scan({ enabled: true }));
    }
  }, []);

  useEffect(() => {
    // Load Google Maps JavaScript API
    const script = document.createElement('script');
    script.text = `(g=>{var h,a,k,p="The Google Maps JavaScript API",c="google",l="importLibrary",q="__ib__",m=document,b=window;b=b[c]||(b[c]={});var d=b.maps||(b.maps={}),r=new Set,e=new URLSearchParams,u=()=>h||(h=new Promise(async(f,n)=>{await (a=m.createElement("script"));e.set("libraries",[...r]+"");for(k in g)e.set(k.replace(/[A-Z]/g,t=>"_"+t[0].toLowerCase()),g[k]);e.set("callback",c+".maps."+q);a.src=\`https://maps.\${c}apis.com/maps/api/js?\`+e;d[q]=f;a.onerror=()=>h=n(Error(p+" could not load."));a.nonce=m.querySelector("script[nonce]")?.nonce||"";m.head.append(a)}));d[l]?console.warn(p+" only loads once. Ignoring:",g):d[l]=(f,...n)=>r.add(f)&&u().then(()=>d[l](f,...n))})({
      key: "AIzaSyAwNd9A7-DqCDa_X4BO3mIBMR9c8aX4eVQ",
      v: "weekly"
    });`;
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  // Returns whether the backend answered, so the caller doesn't read stale state.
  const checkBackendServices = async () => {
    try {
      const { error } = await supabase
        .from('core_entities')
        .select('id')
        .limit(1);
      setIsBackendAvailable(!error);
      return !error;
    } catch (error) {
      console.error('Backend services are unavailable:', error);
      setIsBackendAvailable(false);
      return false;
    }
  };

  // Subscribe once; the handler only uses state setters, which are stable.
  useEffect(() => {
    const handleAuthChange = (event, session) => {
      if (
        event === 'SIGNED_IN' ||
        event === 'SIGNED_OUT' ||
        event === 'MFA_CHALLENGE_VERIFIED' ||
        event === 'PASSWORD_RECOVERY'
      ) {
        setSession(keepIfSameSession(session));
        if (event === 'SIGNED_IN' || event === 'MFA_CHALLENGE_VERIFIED') {
          UserProfile.getByAuthIdLean(session.user.id).then(
            async activeProfile => {
              setUserProfile(keepIfSameProfile(activeProfile));

              if (!activeProfile || !activeProfile?.admin) {
                window.location.href =
                  'https://portal.pensiontracingservice.com';
              }

              Sentry.setUser({
                email: activeProfile?.email,
                fullName: activeProfile?.fullName
              });
              const {
                data: { currentLevel, nextLevel }
              } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();

              if (currentLevel === 'aal1' && nextLevel === 'aal2') {
                // User has MFA enrolled but needs to verify
                setShowMFA(true);
              } else if (currentLevel === 'aal2' && nextLevel === 'aal2')
                setShowMFA(false);
            },
            error => {
              console.error('Error fetching user profile:', error);
            }
          );
        } else if (event === 'PASSWORD_RECOVERY') {
          setPasswordRecovery(true);
        } else {
          setUserProfile(null);
          //redirect('/login');
        }
      }
    };

    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange(handleAuthChange);
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const checkSession = async () => {
      const backendAvailable = await checkBackendServices();
      if (!backendAvailable) {
        setIsLoading(false);
        return;
      }

      const {
        data: { session }
      } = await supabase.auth.getSession();
      setSession(keepIfSameSession(session));

      if (session) {
        // Throws on failure, as getByAuthId did.
        const activeProfile = await UserProfile.getByAuthIdLean(session.user.id);

        if (!activeProfile || !activeProfile?.admin) {
          window.location.href = 'https://portal.pensiontracingservice.com';
        }

        setUserProfile(keepIfSameProfile(activeProfile));

        Sentry.setUser({
          email: activeProfile?.email,
          fullName: activeProfile?.fullName
        });
        const userPreferences =
          JSON.parse(localStorage.getItem('user_preferences')) || {};
        setTheme(userPreferences.theme || 'light');
      }

      setIsLoading(false);
    };

    checkSession();
  }, []);

  // The route tree only depends on whether there is a session and whether MFA
  // is pending. Rebuilding the router on any other state change would remount
  // the whole app (lost forms/popups when Supabase re-fires SIGNED_IN).
  const hasSession = !!session;
  const router = useMemo(
    () =>
      sentryCreateBrowserRouter([
        {
          path: '/',
          errorElement: <Error />,
          element: (
            <Suspense fallback={<LogoLoader logo={'/logo-star.png'} />}>
              <Outlet />
            </Suspense>
          ),
          children: [
            {
              path: 'connect-xero',
              element: hasSession ? <XeroCallback /> : <Navigate to='/login' />
            },
            {
              index: true,
              element: !hasSession ? (
                <Navigate to='/login' />
              ) : showMFA ? (
                <Navigate to='/mfa' />
              ) : (
                <Dashboard />
              )
            },
            {
              path: 'login',
              element: !hasSession ? (
                <Login />
              ) : showMFA ? (
                <Navigate to='/mfa' />
              ) : (
                <Navigate to='/' />
              )
            },
            {
              path: 'mfa',
              element: !hasSession ? (
                <Navigate to='/login' />
              ) : !showMFA ? (
                <Navigate to='/' />
              ) : (
                <MFA />
              )
            },
            {
              path: 'password',
              element: !hasSession ? <ForgotPassword /> : <Navigate to='/' />
            },
            {
              // Always reachable: redeeming a recovery link creates a session, and
              // signed-in users may change their own password here too.
              path: 'resetPassword',
              element: <ResetPassword />
            },
            {
              path: 'signup',
              element: !hasSession ? <Signup /> : <Navigate to='/' />
            },
            {
              path: 'styles',
              element: !hasSession ? (
                <Navigate to='/login' />
              ) : showMFA ? (
                <Navigate to='/mfa' />
              ) : (
                <StylePages />
              )
            },
            {
              path: '/*',
              element: !hasSession ? (
                <Navigate to='/login' />
              ) : showMFA ? (
                <Navigate to='/mfa' />
              ) : (
                <Dashboard />
              )
            }
          ]
        }
      ]),
    [hasSession, showMFA]
  );

  // Stop a replaced router listening to history once the new one takes over.
  useEffect(() => () => router.dispose(), [router]);

  const userProfileValue = useMemo(
    () => ({ userProfile, setUserProfile }),
    [userProfile]
  );
  const themeValue = useMemo(() => ({ theme, setTheme }), [theme]);

  if (isLoading) return <LogoLoader logo={'/logo-star.png'} />;
  if (showLoadingScreen) return <Loading />;
  if (!isBackendAvailable) return <MaintenancePage />;

  return (
    <UserProfileContext.Provider value={userProfileValue}>
      <NotificationProvider>
        <ThemeContext.Provider value={themeValue}>
          <AiProvider
            handleAiRequest={handleAiRequest}
            handleVoiceRequest={handleVoiceRequest}
          >
            <ChatPanelProvider>
              <RouterProvider router={router} />
              <Toaster />
            </ChatPanelProvider>
          </AiProvider>
        </ThemeContext.Provider>
      </NotificationProvider>
    </UserProfileContext.Provider>
  );
};

export default App;
